import React, { createContext, useState, ReactNode, useEffect } from "react";
import { initializeApp, FirebaseApp } from "firebase/app";
import { getStorage, FirebaseStorage } from "firebase/storage";
import { getDatabase, Database, ref, onValue } from "firebase/database";
import { getAuth, signInWithEmailAndPassword, Auth } from "firebase/auth";
import { FrontConfigurationsFirebaseConfig } from "../../configurations/Axios/urls";
import { useAuth } from "../AuthContext/AuthContext";
import useAxios from "../AxiosContext/AxiosContext";
import useFirebaseStorageHelper from "./hooks/useFirebaseStorageHelper";
import { FirebaseStorageHelper } from "./hooks/useFirebaseStorageHelper";
import useFirebaseRealtimeHelper from "./hooks/useFirebaseRealTimeHelpet";
import { FirebaseRealtimeHelper } from "./hooks/useFirebaseRealTimeHelpet";
import uselogs from "./hooks/uselogs";

/**
 * Shape of the object provided by {@link FirebaseContext}.
 */
export interface useFirebasereturn {
  firebasestorage: FirebaseStorageHelper;
  firebaserealtime: FirebaseRealtimeHelper;
  logoutFirebase: () => Promise<void>;
}

/**
 * React context holding Firebase helpers.
 */
export const FirebaseContext = createContext<useFirebasereturn | undefined>(
  undefined
);

/**
 * Initializes Firebase services and exposes helpers via context.
 *
 * @param children - Components that will consume the Firebase context.
 * @returns JSX provider element.
 */
export const FirebaseProvider = ({ children }: { children: ReactNode }) => {
  const [app, setApp] = useState<FirebaseApp | null>(null);
  const [auth, setAuth] = useState<Auth | null>(null);
  const [storage, setStorage] = useState<FirebaseStorage | null>(null);
  const [database, setDatabase] = useState<Database | null>(null);
  const [firebaseConfiguration, setFirebaseConfiguration] = useState<
    any | null
  >(null);

  const firebasestorage = useFirebaseStorageHelper(storage);
  const firebaserealtime = useFirebaseRealtimeHelper(database);
  const { user, logout } = useAuth();
  const { VisitaxGet } = useAxios();

  const now = new Date();

  const formattedDate = now
    .toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
    .replace(/\//g, "-");

  const formattedTime = now
    .toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();

  uselogs({ firebaserealtime, database, user });

  const authenticateWithEmailAndPassword = async (
    email: string,
    password: string
  ) => {
    if (auth && database) {
      try {
        const result = await signInWithEmailAndPassword(auth, email, password);
        const firebaseUid = result.user.uid;
        const LoginUser = {
          firebaseUid: firebaseUid,
          loggedIn: true,
          date: `${formattedDate} ${formattedTime}`,
          userName:
            user?.name +
            " " +
            user?.lastName +
            " " +
            user?.fatherLastName +
            " " +
            user?.motherLastName,
        };
        firebaserealtime.setData(`Login/${user?.id}`, LoginUser);
        console.log("✅ Login exitoso y estado guardado en:", firebaseUid);
      } catch (error) {
        console.error("❌ Error al autenticar:", error);
        throw error;
      }
    }
  };

  const logoutFirebase = async () => {
    if (auth && database && auth.currentUser) {
      const LoginUser = {
        loggedIn: false,
        date: `${formattedDate} ${formattedTime}`,
      };
      firebaserealtime.updateData(`Login/${user?.id}`, LoginUser);
    }
  };

  const GetFirebaseConfigurations = () => {
    const onFirebaseConfigResponse = (response: any) => {
      if (response.status === 200) {
        const Configurations = response.data.data;
        setFirebaseConfiguration(Configurations);
      }
    };
    VisitaxGet(FrontConfigurationsFirebaseConfig, onFirebaseConfigResponse);
  };

  useEffect(() => {
    if (user && user.token) {
      GetFirebaseConfigurations();
    }
  }, [user]);

  useEffect(() => {
    if (firebaseConfiguration) {
      setApp(initializeApp(firebaseConfiguration.firebaseConfig));
    }
  }, [firebaseConfiguration]);

  useEffect(() => {
    if (app) {
      setAuth(getAuth(app));
      setStorage(getStorage(app));
      setDatabase(getDatabase(app));
    }
  }, [app]);

  useEffect(() => {
    if (auth && user && firebaseConfiguration) {
      authenticateWithEmailAndPassword(
        user.userName,
        atob(firebaseConfiguration.paswordFirebase)
      );
    }
  }, [auth, user, firebaseConfiguration]);

  useEffect(() => {
    if (database && auth?.currentUser) {
      const loginStatusRef = ref(database, `Login/${user?.id}/loggedIn`);

      const unsubscribe = onValue(loginStatusRef, async (snapshot) => {
        const isLoggedIn = snapshot.val();
        console.log("🔥 Estado en Firebase cambió:", isLoggedIn);

        if (isLoggedIn === false) {
          console.warn("🚨 Logout forzado detectado desde Firebase");
          await logout();
        }
      });

      return () => unsubscribe();
    }
  }, [database, auth?.currentUser]);

  return (
    <FirebaseContext.Provider
      value={{ firebasestorage, firebaserealtime, logoutFirebase }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};

/**
 * Hook to access the configured Firebase helpers.
 *
 * @throws Error if used outside of {@link FirebaseProvider}.
 * @returns Firebase utilities for storage and realtime database.
 */
export const useFirebase = () => {
  const context = React.useContext(FirebaseContext);
  if (context === undefined) {
    throw new Error("FirebaseProvider must be used within an AuthProvider");
  }
  return context;
};
